import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePageTitle } from "@/lib/usePageTitle";
import { useApp } from "@/state/AppContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Storefront } from "@/components/storefront/Storefront";

/** Route container: fetch, then hand everything to `<Storefront>` (PR3 of
 * design_handoff_business_websites — see storefront/registry.js and components/storefront/).
 * The route (`/b/:slug` and `/:slug`) and this default export are untouched on purpose, so
 * nothing importing `BusinessMiniSite` had to change. */
export default function BusinessMiniSite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, tenant } = useApp();
  const [business, setBusiness] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [offers, setOffers] = useState([]);
  const [ads, setAds] = useState(null);
  usePageTitle(notFound ? t("business.notFoundTitle") : business?.name); // R4-7: the not-found state gets its own tab title, not the default

  useEffect(() => {
    if (!tenant) return;
    (async () => {
      try {
        const { data } = await api.get(`/directory/businesses/by-slug/${slug}`, { params: { tenant_id: tenant.id } });
        setBusiness(data);
        const { data: offerList } = await api.get(`/businesses/${data.id}/offers`);
        setOffers(offerList);
        // The platform rail ad is new to the storefront (PR3) — skip the fetch entirely when the
        // business has opted out, rather than fetching and then hiding it at render time.
        if (data.storefront_config?.ads?.allow !== false) {
          try {
            const { data: adData } = await api.get("/city/ads", { params: { tenant_id: tenant.id } });
            setAds(adData);
          } catch {
            // Decorative only; a failed ad fetch must never block the storefront from rendering.
          }
        }
      } catch (e) {
        setNotFound(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, tenant]);

  if (notFound)
    return (
      <div className="mx-auto max-w-2xl px-5 py-20 text-center" data-testid="business-not-found">
        <h1 className="font-heading text-2xl font-semibold">{t("business.notFoundTitle")}</h1>
        <p className="mt-3 text-sm text-muted-foreground">{t("business.notFoundBody")}</p>
        <Button className="mt-6 rounded-full" data-testid="business-not-found-back" onClick={() => navigate("/")}>
          {t("business.notFoundBack")}
        </Button>
      </div>
    );
  // R4-10: a bare "Loading..." for the 3-5 s a cold request can take read as a broken page - hold
  // the storefront's own shape (hero + body blocks) so it is plainly on its way.
  if (!business)
    return (
      <div data-testid="storefront-loading" aria-busy="true" aria-label={t("common.loading")}>
        <div className="h-[46vh] min-h-[300px] w-full animate-pulse bg-muted" />
        <div className="mx-auto max-w-7xl space-y-4 px-5 py-8 sm:px-8">
          <div className="h-8 w-2/3 animate-pulse rounded-xl bg-muted" />
          <div className="h-40 animate-pulse rounded-2xl bg-muted" />
        </div>
      </div>
    );

  return <Storefront business={business} offers={offers} ads={ads} />;
}
